import { useEffect, useMemo, useRef, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { debounce } from '@zg-rentals/zdash';

import { MULTIPLEX_AMENITY_KEYS } from '../../../../srcLegacy/shared/hooks/queries/multiplex/useMultiplexUpdate';
import { getDataToSave } from '../../../../srcLegacy/shared/utils/amenitiesUtils';
import { getFirstUnitError } from '../../../utils/multiplexUpdateErrorUtils';

import TimeConstants from '../../../../srcLegacy/shared/constants/TimeConstants';
import { DEFAULT_ERROR_MESSAGE } from '../../../constants/ErrorMessageConstants';

import type { UserPropertyMultiplexUpdateRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { UseMultiplexUpdateResult } from '../../../../srcLegacy/shared/hooks/queries/multiplex/useMultiplexUpdate';

const pickMultiplexAmenityPostData = (
  propertyRecord: Record<string, unknown>,
): Partial<UserPropertyMultiplexUpdateRequestWebDto> => {
  const out: Partial<UserPropertyMultiplexUpdateRequestWebDto> = {};
  for (const key of MULTIPLEX_AMENITY_KEYS) {
    const value = propertyRecord[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
};

type Params = {
  listingAlias: string;
  categorizedAmenitiesKeys: Record<string, Array<string>>;
  multiplexBuildingSessionId: string | undefined;
  multiplexUpdateAsync: UseMultiplexUpdateResult['mutateAsync'];
};

/**
 * Debounced multiplex amenity save for the property amenities step.
 * Refs keep the debounced callback stable while always reading latest session/keys (same pattern as useFieldSave).
 */
export const useDebouncedMultiplexAmenitySave = ({
  listingAlias,
  categorizedAmenitiesKeys,
  multiplexBuildingSessionId,
  multiplexUpdateAsync,
}: Params) => {
  const [error, setError] = useState<string | null>(null);

  const latest = useRef({
    listingAlias,
    categorizedAmenitiesKeys,
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    setError,
  });
  latest.current = {
    listingAlias,
    categorizedAmenitiesKeys,
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    setError,
  };

  const saveDebounced = useMemo(
    () =>
      debounce((inputsToSave: Record<string, boolean | string>) => {
        const {
          listingAlias: alias,
          categorizedAmenitiesKeys: keys,
          multiplexBuildingSessionId: sessionId,
          multiplexUpdateAsync: updateAsync,
          setError: setSaveError,
        } = latest.current;

        if (!sessionId) {
          logError({
            error: new Error('Unable to save: missing multiplexBuildingSessionId'),
            context: { details: 'useDebouncedMultiplexAmenitySave' },
          });
          return;
        }

        const dataToSave = getDataToSave({ inputs: inputsToSave, categorizedAmenitiesKeys: keys, listingAlias: alias });
        const propertyRecord = (dataToSave.property ?? {}) as unknown as Record<string, unknown>;
        const postData = pickMultiplexAmenityPostData(propertyRecord);
        if (Object.keys(postData).length === 0) {
          return;
        }

        void (async () => {
          try {
            setSaveError(null);
            const response = await updateAsync({ getParams: { multiplexBuildingSessionId: sessionId }, postData });

            const unitErrorMessage = getFirstUnitError(response.units, {
              fieldNames: MULTIPLEX_AMENITY_KEYS,
              fallbackMessage: 'Unable to save amenities.',
              responseFailed: response.success === false,
            });

            if (unitErrorMessage) {
              setSaveError(unitErrorMessage);
            }
          } catch (err: unknown) {
            const message = DatarouterError.is(err)
              ? DatarouterError.getDisplayMessage(err)
              : ((err as { message?: string })?.message ?? DEFAULT_ERROR_MESSAGE);
            setSaveError(message);
          }
        })();
      }, TimeConstants.SELECTION_DEBOUNCE_TIME_MS),
    [],
  );

  useEffect(() => () => saveDebounced.cancel(), [saveDebounced]);

  return { saveDebounced, error };
};
